import { WebViewerInstance } from '@pdftron/webviewer';
import type { VirtualElement } from '@popperjs/core';
import { useRulesDetailModalContext } from 'contexts/RulesDetailModalContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  PART_BOT_RESULTS_BASE_QUERY_KEY,
  PartBotResultJson,
  RunesCallout,
  RunesRegion,
  useDeletePrintExtraction,
  useRegenerateRequirementsReview,
  type Rule,
} from '@frontend/api';
import {
  Portal,
  Box,
  VStack,
  Button,
  Text,
  Divider,
  AssistanceRejectedIcon,
  ShareExternalIcon,
  useToast,
  ButtonGroup,
  Spinner,
  Flex,
} from '@frontend/components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import {
  learnMoreUrl,
  learnMoreClick,
} from 'modules/quotes/quoteItems/components/PartExpression/partbotLearnMore';

import { useFeatureFlagValue } from 'store/session/hooks';

import { useAnnotationFlyoutActions } from './AnnotationFlyoutActionContext';
import { ISuggestionAnnotation } from './annotationCommands/SuggestionAnnotation';
import { SUGGESTION_ANNOTATION_COMMAND_TYPES } from './annotationCommands/consts';
import {
  FoundInFileMetadata,
  FieldSuggestionMetadata,
  ANNOTATION_METADATA_TYPES,
} from './annotationCommands/types';
import { suggestionAnnotations } from './data/actions';

type SuggestionAnnotationFlyoutProps = {
  instance: WebViewerInstance | null;
  isOpen: boolean;
  virtualReference: VirtualElement | null;
  annotation: ISuggestionAnnotation | null;
  onClose: () => void;
};

export const SuggestionAnnotationFlyout = ({
  instance,
  isOpen,
  virtualReference,
  annotation,
  onClose,
}: SuggestionAnnotationFlyoutProps) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const toast = useToast();
  const { openModal } = useRulesDetailModalContext();
  const isRequirementsReviewEnabled = useFeatureFlagValue(
    featureFlagKeys.isRequirementsReviewEnabled,
  );
  const { getActions } = useAnnotationFlyoutActions();

  // Get quoteItemId from URL params
  const location = useLocation();
  const quoteItemId = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    const quoteItemIdParam = urlParams.get('quote_item_id');
    return quoteItemIdParam ? parseInt(quoteItemIdParam, 10) : undefined;
  }, [location.search]);

  // Use direct API call for regeneration
  const { mutate: regenerateReqsReview } = useRegenerateRequirementsReview({
    queryOptions: {
      onError: () => {
        toast({
          title: 'Failed to reload rules',
          status: 'error',
        });
      },
    },
  });

  // NOTE: the side effect of a scroll event triggers the focus grab and release lifecycle of
  // the document viewer which we need to trigger to prevent persistent focus fighting and
  // breaking tab behavior when modals and dialogs open over the PDF viewer. Triggering a scroll
  // event essentially tricks the PDF document to elegantly release focus from the document
  // viewer to the surrounding application
  const triggerDocumentReleaseFocus = useCallback(() => {
    if (!instance?.Core.documentViewer) return;
    const scrollView = instance.Core.documentViewer.getScrollViewElement();
    if (scrollView) {
      scrollView.dispatchEvent(new Event('scroll', { bubbles: true }));
      console.warn(
        '[SuggestionAnnotationFlyout] Scroll event dispatched to reset focusing',
      );
    }
  }, [instance?.Core.documentViewer]);

  const { styles, attributes } = usePopper(virtualReference, popperElement, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: [
            'top-start',
            'bottom-start',
            'right-start',
            'left-start',
          ],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 10,
        },
      },
    ],
  });

  const properties: {
    description?: string;
    filename?: string;
    pageNumber?: number;
    partBotResult?: PartBotResultJson;
    rawCallout?: RunesCallout | null;
    rawRegion?: RunesRegion | null;
    value?: string;
    shouldShow: boolean;
  } = useMemo(() => {
    switch (annotation?.rawAnnotation?.metadata?.type) {
      case ANNOTATION_METADATA_TYPES.FOUND_IN_FILES: {
        const metadata = annotation?.rawAnnotation
          ?.metadata as FoundInFileMetadata;
        return {
          description: metadata.partBotResult.description,
          filename: metadata.partBotResult.filename,
          pageNumber: annotation?.getPageNumber?.(),
          partBotResult: metadata.partBotResult,
          rawCallout: metadata.partBotResult.rawCallout,
          rawRegion: metadata.partBotResult.rawRegion,
          value: metadata.partBotResult.value,
          shouldShow: true,
        };
      }
      case ANNOTATION_METADATA_TYPES.FIELD_SUGGESTION: {
        const metadata = annotation?.rawAnnotation
          ?.metadata as FieldSuggestionMetadata;
        return {
          description: String(metadata.fieldSuggestion.suggestion),
          filename: metadata.fieldSuggestion.sourceName,
          pageNumber: annotation?.getPageNumber?.(),
          shouldShow: true,
        };
      }
      case ANNOTATION_METADATA_TYPES.EXTRACTED_TABLE:
      case ANNOTATION_METADATA_TYPES.ACTION_EXTRACTION_TEXT:
      default:
        return {
          shouldShow: false,
        };
    }
  }, [annotation]);

  const {
    description,
    filename,
    pageNumber,
    rawCallout,
    value,
    partBotResult,
    shouldShow,
  } = properties;

  const onRuleCreateSuccess = useCallback(
    (data: Rule) => {
      toast({
        title: 'Created Rule',
        description: `Rule "${data.name}" created successfully`,
        status: 'success',
      });
      // Trigger backend regeneration for autorefresh
      if (quoteItemId) {
        regenerateReqsReview({ quoteItemId: String(quoteItemId) });
      }
    },
    [toast, regenerateReqsReview, quoteItemId],
  );

  const handleCreateRule = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      onClose();

      if (rawCallout) {
        openModal({
          rawCallout,
          onSuccess: onRuleCreateSuccess,
          mode: 'create',
          onTriggerSideEffect: triggerDocumentReleaseFocus,
        });
      } else if (value) {
        openModal({
          selectedText: value,
          onSuccess: onRuleCreateSuccess,
          mode: 'create',
          onTriggerSideEffect: triggerDocumentReleaseFocus,
        });
      }
    },
    [
      rawCallout,
      value,
      openModal,
      onRuleCreateSuccess,
      onClose,
      triggerDocumentReleaseFocus,
    ],
  );

  const queryClient = useQueryClient();

  const { mutate: deletePrintExtraction } = useDeletePrintExtraction({
    queryOptions: {
      onSuccess: () => {
        // invalidate all part bot queries since we do not have convenient access to the
        // part uuid
        queryClient.invalidateQueries(PART_BOT_RESULTS_BASE_QUERY_KEY);
      },
    },
  });
  const handleDelete = useCallback(() => {
    if (!partBotResult?.uuid) return;
    deletePrintExtraction({ printExtractionUuid: partBotResult.uuid });
    // Remove the finding from context
    onClose();
  }, [partBotResult?.uuid, deletePrintExtraction, onClose]);

  const annotationActions = useMemo(
    () =>
      annotation
        ? getActions(annotation)
        : { geometric: null, partDescriptor: {} },
    [annotation, getActions],
  );

  const { geometric: geometricActions, partDescriptor: partDescriptorActions } =
    annotationActions;
  const annotationId = annotation?.Id;
  const equalDimensions = useMemo(
    () =>
      annotation && geometricActions?.getEqualDimensions
        ? geometricActions.getEqualDimensions(annotation)
        : [],
    [annotation, geometricActions],
  );

  const dispatch = useDispatch();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popperElement && !popperElement.contains(event.target as Node)) {
        onClose();
        if (annotationId) {
          // deselect the annotation
          dispatch(
            suggestionAnnotations.queueCommands({
              commands: [
                {
                  type: SUGGESTION_ANNOTATION_COMMAND_TYPES.UNFOCUS,
                  annotationIds: [annotationId],
                },
              ],
            }),
          );
        }
      }
      triggerDocumentReleaseFocus();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    isOpen,
    onClose,
    popperElement,
    annotationId,
    dispatch,
    triggerDocumentReleaseFocus,
  ]);

  if (!isOpen || !virtualReference || !annotation || !shouldShow) {
    return null;
  }

  return (
    <Portal>
      <Box
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
        zIndex={10000}
        bg="white"
        borderRadius="lg"
        boxShadow="popover"
        minWidth={24}
        maxWidth={50}
        border="1px solid"
        borderColor="separator.border"
      >
        <VStack spacing={0} align="stretch">
          <VStack px={1.5} py={1} align="stretch" spacing={0.75}>
            {description && <Text>{description}</Text>}
            <Text fontSize="sm" color="feature.secondary">
              {`Found on page ${pageNumber ?? 'unknown'} of ${filename}`}
            </Text>
          </VStack>
          <Divider />
          {geometricActions && (
            <>
              <Box px={1.5} py={1} display="flex" flexDirection="column">
                <ButtonGroup isAttached size="sm" alignItems="center">
                  <Text size="sm">Set as&nbsp;</Text>
                  <Button
                    color={
                      equalDimensions.includes('x') ? undefined : 'geometry.x'
                    }
                    onClick={() => geometricActions.setSizeX.fn(annotation)}
                    variant={
                      equalDimensions.includes('x') ? 'primary' : 'secondary'
                    }
                  >
                    X
                  </Button>
                  <Button
                    color={
                      equalDimensions.includes('y') ? undefined : 'geometry.y'
                    }
                    onClick={() => geometricActions.setSizeY.fn(annotation)}
                    variant={
                      equalDimensions.includes('y') ? 'primary' : 'secondary'
                    }
                  >
                    Y
                  </Button>
                  <Button
                    color={
                      equalDimensions.includes('z') ? undefined : 'geometry.z'
                    }
                    onClick={() => geometricActions.setSizeZ.fn(annotation)}
                    variant={
                      equalDimensions.includes('z') ? 'primary' : 'secondary'
                    }
                  >
                    Z
                  </Button>
                  {(geometricActions.setSizeX.isLoading ||
                    geometricActions.setSizeY.isLoading ||
                    geometricActions.setSizeZ.isLoading) && (
                    <Spinner size="sm" ml={1} />
                  )}
                </ButtonGroup>
              </Box>
              <Divider />
            </>
          )}
          {partDescriptorActions &&
            (partDescriptorActions.setPartNumber ||
              partDescriptorActions.setRevision ||
              partDescriptorActions.setDescription) && (
              <>
                <Flex px={1.5} py={1} alignItems="center">
                  {partDescriptorActions.setPartNumber && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() =>
                        partDescriptorActions.setPartNumber?.fn(annotation)
                      }
                    >
                      Set as part number
                    </Button>
                  )}
                  {partDescriptorActions.setRevision && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() =>
                        partDescriptorActions.setRevision?.fn(annotation)
                      }
                    >
                      Set as revision
                    </Button>
                  )}
                  {partDescriptorActions.setDescription && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() =>
                        partDescriptorActions.setDescription?.fn(annotation)
                      }
                    >
                      Set as description
                    </Button>
                  )}
                  {Boolean(
                    partDescriptorActions.setPartNumber?.isLoading ||
                      partDescriptorActions.setRevision?.isLoading ||
                      partDescriptorActions.setDescription?.isLoading,
                  ) && <Spinner size="sm" ml={1} />}
                </Flex>
                <Divider />
              </>
            )}
          <Box px={1.5} py={1} display="flex" gap={2}>
            {partBotResult?.uuid && (
              <Button
                colorScheme="destructive"
                variant="link"
                size="sm"
                icon={<AssistanceRejectedIcon />}
                onClick={handleDelete}
              >
                Mark as inaccurate
              </Button>
            )}
            {isRequirementsReviewEnabled &&
            partBotResult &&
            partBotResult.canAuthorRule ? (
              <Button
                colorScheme="default"
                variant="link"
                size="sm"
                onClick={handleCreateRule}
              >
                Create rule
              </Button>
            ) : (
              <Button
                colorScheme="default"
                variant="link"
                size="sm"
                icon={<ShareExternalIcon />}
                as="a"
                onClick={learnMoreClick}
                href={`${learnMoreUrl}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </Button>
            )}
          </Box>
        </VStack>
      </Box>
    </Portal>
  );
};
